import "./Button.scss";

import clsx from "clsx";
import React, { FC, ReactNode } from "react";
import { Link, LinkProps } from "react-router-dom";
import { FCC } from "types";

import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner.tsx";

type ButtonType = "submit" | "button" | "reset";
type ButtonVariant = "primary" | "secondary" | "tertiary";
type ButtonSize = "sm" | "lg";

// "eSIM" is a fixed expression with a lowercase "e" - the automatic
// first-letter capitalization below must not turn it into "ESIM".
const FIXED_CASING_LABEL_PATTERN = /^esim/i;

const getBtnLabelClassName = (children: ReactNode) =>
  clsx(
    "btn-label",
    typeof children === "string" &&
      FIXED_CASING_LABEL_PATTERN.test(children) &&
      "btn-label--preserve-case",
  );

/**
 * Class names of the design-system button, exported for the rare control that
 * has to be a plain anchor (an external link) and therefore cannot use
 * `Button` or `ButtonLink`.
 */
export const getBtnClassName = ({
  className,
  variant = "primary",
  size = "lg",
  inverted,
  fullWidth,
  disabled,
}: {
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  inverted?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
}) =>
  clsx(
    "btn",
    `btn-${variant}`,
    `btn-${size}`,
    inverted && "btn-inverted",
    fullWidth && "w-full",
    disabled && "disabled",
    className,
  );

/**
 * A control without visible text has to name itself, so `ariaLabel` is only
 * optional as long as there are children to read out. Without children it is
 * required, which makes an unnamed icon-only button a compile error.
 */
type LabelledProps =
  | { children: ReactNode; ariaLabel?: string }
  | { children?: never; ariaLabel: string };

type ButtonProps = LabelledProps & {
  dataTestid?: string;
  className?: string;
  disabled?: boolean;
  type?: ButtonType;
  /** Submits/resets a `<form>` this button isn't nested inside, by id. */
  form?: string;
  fullWidth?: boolean;
  loading?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  inverted?: boolean;
  icon?: ReactNode;
  pressed?: boolean;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void | Promise<void>;
  /**
   * For a control that toggles an input it must not steal focus from - the
   * handler calls `preventDefault()` so the input keeps focus on mousedown.
   */
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Set by HintTooltip so the reason is announced on focus. */
  "aria-describedby"?: string;
  /** For a button that toggles a disclosure - e.g. the "show code" toggle. */
  "aria-expanded"?: boolean;
  "aria-controls"?: string;
};

export const Button: FC<ButtonProps> = ({
  dataTestid,
  className,
  children,
  disabled,
  // A `<button>` with no explicit type defaults to "submit" inside a <form> -
  // every caller that actually wants that (Save/Confirm) already passes
  // type="submit" itself, so every other button must default to "button" or
  // it silently submits (and resets) the surrounding form on click.
  type = "button",
  form,
  fullWidth,
  loading,
  variant = "primary",
  size = "lg",
  inverted,
  icon,
  pressed,
  ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-expanded": ariaExpanded,
  "aria-controls": ariaControls,
  onClick = () => null,
  onMouseDown,
}) => {
  const iconOnly = !children && !!icon;

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      // Disabled-with-a-tooltip-reason keeps the native `disabled` attribute
      // off (see nativeDisabled below), so without this the browser would
      // still run a type="submit"/"reset" button's default form action.
      event.preventDefault();

      return;
    }

    try {
      await onClick(event);
    } catch (error) {
      console.error("Button onClick error:", error);
    }
  };

  // A tooltip reason (HintTooltip sets aria-describedby) has to stay
  // reachable by keyboard, which the native `disabled` attribute prevents
  // outright - same non-native-disabled trick ButtonLink already uses.
  // Loading is transient and carries no reason to announce, so it keeps
  // the native attribute.
  const nativeDisabled = loading || (disabled && !ariaDescribedBy);

  return (
    <div className={clsx(fullWidth ? "flex w-full" : "inline-flex")}>
      <button
        data-testid={dataTestid}
        className={clsx(
          getBtnClassName({
            variant,
            size,
            inverted,
            fullWidth,
            disabled,
            className,
          }),
          iconOnly && "btn-icon-only",
          pressed && "pressed",
        )}
        type={type}
        form={form}
        tabIndex={nativeDisabled ? -1 : 0}
        onClick={handleClick}
        // Same guard as handleClick: a non-natively-disabled button (one with
        // a tooltip reason) still dispatches mousedown, and a disabled control
        // must not run its consumer's handler.
        onMouseDown={(event) => {
          if (disabled || loading) {
            return;
          }

          onMouseDown?.(event);
        }}
        aria-busy={loading}
        aria-disabled={disabled || loading}
        aria-pressed={pressed}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        disabled={nativeDisabled}
      >
        <span className="btn-bg" aria-hidden="true" />
        {loading && (
          <LocalLoadingSpinner className="button-loading-spinner relative z-10" />
        )}
        {children && (
          <span className={getBtnLabelClassName(children)}>{children}</span>
        )}
        {icon && <span className="btn-icon">{icon}</span>}
      </button>
    </div>
  );
};

type AnchorProps = {
  className?: string;
  href?: string;
  disabled?: boolean;
  label?: string;
  target?: "_self" | "_blank" | "_parent" | "_top";
};
export const Anchor = ({
  className = "",
  href = "/",
  disabled = false,
  label = "",
  target = "_blank",
}: AnchorProps) => {
  return (
    <div id="navigate-button-wrapper" className="button-wrapper font-semibold">
      <a
        aria-label={label}
        href={href}
        className={clsx(className, disabled && "disabled")}
        target={target}
        rel="noreferrer"
        // Anchors are natively draggable. Pressing, dragging off and releasing
        // elsewhere starts the browser's drag-and-drop loop for the URL, which
        // makes the page appear frozen. Plain buttons are not draggable and are
        // therefore unaffected.
        draggable={false}
      >
        {label}
      </a>
    </div>
  );
};

export const StyledLink: FCC<
  LinkProps &
    React.RefAttributes<HTMLAnchorElement> & {
      disabled?: boolean;
      variant?: "default" | "outlined";
      "data-testid"?: string;
    }
> = ({ className, children, disabled, variant, ...props }) => {
  if (disabled) {
    return (
      <div className="fakebutton-wrapper button-wrapper font-semibold">
        <div
          data-testid={props["data-testid"] ?? undefined}
          className={clsx(
            "fakebutton disabled mt-14 w-full smPlus:w-fit",
            variant === "outlined" ? "fakebutton-outlined" : "accent",
            className,
          )}
        >
          {children}
        </div>
      </div>
    );
  }

  if (variant === "outlined") {
    return (
      <Link
        className={clsx(
          "border-2 border-highlight-100 bg-white px-8 py-3 text-center font-semibold text-highlight-100 transition-colors hover:bg-highlight-100 hover:text-white",
          className,
        )}
        draggable={false}
        {...props}
      >
        {children}
      </Link>
    );
  }

  return (
    <div id="styled-link-wrapper" className="button-wrapper font-semibold">
      <Link className={className} draggable={false} {...props}>
        {children}
      </Link>
    </div>
  );
};

export const ButtonLink: FC<
  Omit<LinkProps, "children"> &
    React.RefAttributes<HTMLAnchorElement> &
    LabelledProps & {
      disabled?: boolean;
      fullWidth?: boolean;
      variant?: ButtonVariant;
      size?: ButtonSize;
      inverted?: boolean;
      icon?: ReactNode;
      /** Set by HintTooltip so the reason is announced on focus. */
      "aria-describedby"?: string;
      "data-testid"?: string;
    }
> = ({
  className,
  children,
  disabled,
  fullWidth,
  variant = "primary",
  size = "lg",
  inverted,
  icon,
  ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "data-testid": dataTestid,
  ...props
}) => {
  const iconOnly = !children && !!icon;
  const btnClassName = clsx(
    getBtnClassName({
      variant,
      size,
      inverted,
      fullWidth,
      className,
      disabled,
    }),
    iconOnly && "btn-icon-only",
  );

  const content = (
    <>
      <span className="btn-bg" aria-hidden="true" />
      {children && (
        <span className={getBtnLabelClassName(children)}>{children}</span>
      )}
      {icon && <span className="btn-icon">{icon}</span>}
    </>
  );

  if (disabled) {
    return (
      <div className={clsx(fullWidth ? "flex w-full" : "inline-flex")}>
        <span
          data-testid={dataTestid}
          className={btnClassName}
          // Focusable so keyboard users reach the disabled control and hear
          // the tooltip that explains why it is unavailable.
          tabIndex={ariaDescribedBy ? 0 : undefined}
          role={ariaDescribedBy ? "button" : undefined}
          aria-disabled="true"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
        >
          {content}
        </span>
      </div>
    );
  }

  return (
    <div className={clsx(fullWidth ? "flex w-full" : "inline-flex")}>
      <Link
        data-testid={dataTestid}
        className={btnClassName}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        // See Anchor: prevent the native link drag that freezes the page when
        // the pointer is pressed, dragged off, and released elsewhere.
        draggable={false}
        {...props}
      >
        {content}
      </Link>
    </div>
  );
};

export const SubmitButton = ({
  dataTestid,
  className,
  label,
  disabled,
  fullWidth = true,
  size = "lg",
}: {
  dataTestid?: string;
  className?: string;
  label: ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: ButtonSize;
}) => (
  <Button
    dataTestid={dataTestid}
    className={className}
    variant="primary"
    type="submit"
    disabled={disabled}
    fullWidth={fullWidth}
    size={size}
  >
    {label}
  </Button>
);
